/**
 * DataGridContextMenu
 *
 * Right-click context menu for data grid cells.
 * Provides: Copy, Cut (range), Paste, Clear, separator, View detail.
 * When a multi-cell range is selected, labels show cell count and View detail is hidden.
 */

import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from '@/components/ui/context-menu';
import type { DataGridLabels } from './dataGridLabels';
import type { RangeSize } from './types';

interface DataGridContextMenuProps {
  children: ReactNode;
  onCopy: () => void;
  onPaste: () => void;
  onClear: () => void;
  onViewDetail: () => void;
  onCut?: () => void;
  /** Called when the menu opens/closes — use to set active cell on right-click */
  onOpenChange?: (open: boolean) => void;
  isEditable: boolean;
  labels?: Partial<DataGridLabels>;
  /** When multi-cell range is active, shows range-aware labels */
  rangeSize?: RangeSize | null;
}

export function DataGridContextMenu({
  children,
  onCopy,
  onPaste,
  onClear,
  onViewDetail,
  onCut,
  onOpenChange,
  isEditable,
  labels,
  rangeSize,
}: DataGridContextMenuProps) {
  const isRange = rangeSize && rangeSize.total > 1;
  const copyLabel = isRange
    ? `${labels?.dgCopy ?? 'Copy'} ${rangeSize.total} ${labels?.dgCells ?? 'cells'}`
    : (labels?.dgCopy ?? 'Copy');
  const clearLabel = isRange
    ? `${labels?.dgClearCell ?? 'Clear'} ${rangeSize.total} ${labels?.dgCells ?? 'cells'}`
    : (labels?.dgClearCell ?? 'Clear cell');
  const cutLabel = isRange
    ? `${labels?.dgCut ?? 'Cut'} ${rangeSize.total} ${labels?.dgCells ?? 'cells'}`
    : (labels?.dgCut ?? 'Cut');

  return (
    <ContextMenu onOpenChange={onOpenChange}>
      <ContextMenuTrigger asChild>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem onClick={onCopy}>
          {copyLabel}
          <ContextMenuShortcut>⌘C</ContextMenuShortcut>
        </ContextMenuItem>
        {isEditable && isRange && onCut && (
          <ContextMenuItem onClick={onCut}>
            {cutLabel}
            <ContextMenuShortcut>⌘X</ContextMenuShortcut>
          </ContextMenuItem>
        )}
        {isEditable && (
          <ContextMenuItem onClick={onPaste}>
            {labels?.dgPaste ?? 'Paste'}
            <ContextMenuShortcut>⌘V</ContextMenuShortcut>
          </ContextMenuItem>
        )}
        {isEditable && (
          <ContextMenuItem onClick={onClear}>
            {clearLabel}
          </ContextMenuItem>
        )}
        {!isRange && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onViewDetail}>
              {labels?.dgViewDetail ?? 'View detail'}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
